// Google-Aufnahme je Website (Search Console, nur lesen) – wie bei .com: „Mit Google verbinden“ oder Dienstkonto, Seiten-Status, Auffrischen per Knopf
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Loader2, RefreshCw, KeyRound, Copy, Trash2, ExternalLink, CheckCircle2, CircleDashed, AlertTriangle, Upload, LogIn } from "lucide-react";

const err = (e, f) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : f);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Berlin" }) : "–");
const btn = "flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 transition-colors disabled:opacity-50";
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const redirectUri = () => `${window.location.origin}/auth/google`;
const gc = (path, project) => `https://console.cloud.google.com/${path}${project ? `?project=${project}` : ""}`;
const PENDING_KEY = "sn-gsc-site";

function useIsAdmin() {
  const [admin, setAdmin] = useState(false);
  useEffect(() => { axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`).then((r) => setAdmin(r.data.role === "admin")).catch(() => {}); }, []);
  return admin;
}

function CopyLine({ value, testid }) {
  const [done, setDone] = useState(false);
  return (
    <span className="inline-flex items-center gap-2 flex-wrap">
      <code className="bg-black/40 rounded px-2 py-1 break-all" data-testid={testid}>{value}</code>
      <button className={btn} onClick={() => { navigator.clipboard?.writeText(value); setDone(true); setTimeout(() => setDone(false), 1800); }} data-testid={`${testid}-copy`}>
        <Copy size={12} /> {done ? "Kopiert" : "Kopieren"}</button>
    </span>
  );
}

function OAuthSteps({ project, slug }) {
  const rows = [
    ["Search Console API einschalten", gc("apis/library/searchconsole.googleapis.com", project), "„Aktivieren“ tippen."],
    ["Weiterleitungs-Adresse eintragen", gc("apis/credentials", project), <>OAuth-Client öffnen → „Autorisierte Weiterleitungs-URIs“ → URI hinzufügen → <CopyLine value={redirectUri()} testid={`gsc-${slug}-redirect-uri`} /> → Speichern.</>],
    ["Dein Google-Konto zulassen", gc("auth/audience", project), "Unter „Testnutzer“ deine Gmail-Adresse eintragen – oder „App veröffentlichen“, dann läuft die Verbindung nicht nach 7 Tagen ab."],
    ["Mit Google verbinden", null, "Knopf unten tippen, Konto wählen und den Zugriff auf die Search Console erlauben."],
  ];
  return (
    <ol className="space-y-2 text-xs mt-3" data-testid={`gsc-${slug}-steps`}>
      {rows.map(([t, href, d], i) => (
        <li key={t} className="flex gap-3">
          <span className="w-5 h-5 rounded-full bg-white/10 grid place-items-center text-[11px] shrink-0">{i + 1}</span>
          <span><b>{href ? <a href={href} target="_blank" rel="noreferrer" className="underline underline-offset-2" data-testid={`gsc-${slug}-step-link-${i + 1}`}>{t}</a> : t}</b>
            <span className="text-eb-muted"> – </span><span className="text-eb-muted">{d}</span></span>
        </li>
      ))}
    </ol>
  );
}

function KeyForm({ api, slug, onSaved }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const file = (e) => { const f = e.target.files?.[0]; if (f) f.text().then(setKey); };
  const save = async () => {
    setBusy(true); setMsg("");
    try { onSaved((await axios.put(`${api}/credentials`, { key })).data); setKey(""); }
    catch (e) { setMsg(err(e, "Datei nicht gespeichert")); }
    setBusy(false);
  };
  return (
    <div className="mt-3 space-y-2" data-testid={`gsc-${slug}-key-form`}>
      <p className="text-xs text-eb-muted">Zuerst die Google-Datei speichern: OAuth-Client (client_secret_….json) – oder alternativ einen Dienstkonto-Schlüssel.</p>
      <label className={`${btn} w-fit cursor-pointer`} data-testid={`gsc-${slug}-key-file-label`}>
        <Upload size={13} /> JSON-Datei auswählen
        <input type="file" accept=".json,application/json" onChange={file} className="sr-only" data-testid={`gsc-${slug}-key-file`} />
      </label>
      <textarea value={key} onChange={(e) => setKey(e.target.value)} rows={3} placeholder='{"web": {"client_id": "…", "client_secret": "…"}}'
        className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-xs font-mono" data-testid={`gsc-${slug}-key-input`} />
      <button className={btn} disabled={busy || key.trim().length < 20} onClick={save} data-testid={`gsc-${slug}-key-save`}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />} Datei speichern</button>
      {msg && <p className="text-xs text-rose-400" data-testid={`gsc-${slug}-key-error`}>{msg}</p>}
    </div>
  );
}

function Badge({ u }) {
  if (u.indexed) return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 flex items-center gap-1 w-fit"><CheckCircle2 size={11} /> aufgenommen</span>;
  if (u.indexed === false) return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 flex items-center gap-1 w-fit"><CircleDashed size={11} /> noch nicht</span>;
  return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/10 text-eb-muted flex items-center gap-1 w-fit"><AlertTriangle size={11} /> unbekannt</span>;
}

function Report({ r, slug }) {
  const sm = r.sitemaps.find((s) => s.path?.includes("sitemap")) || r.sitemaps[0];
  return (
    <div className="mt-4" data-testid={`gsc-${slug}-report`}>
      <div className="flex items-end gap-3 flex-wrap">
        <div className="text-4xl font-heading font-bold text-emerald-300" data-testid={`gsc-${slug}-indexed`}>{r.indexed}</div>
        <div className="text-sm pb-1">von <b data-testid={`gsc-${slug}-total`}>{r.total}</b> Seiten bei Google aufgenommen
          {r.unknown > 0 && <span className="text-eb-muted"> · {r.unknown} nicht prüfbar</span>}</div>
      </div>
      <p className="text-[11px] text-eb-muted mt-1" data-testid={`gsc-${slug}-sitemap-status`}>
        {sm ? `Sitemap eingereicht ${fmt(sm.last_submitted)} · ${sm.submitted} Adressen · zuletzt von Google gelesen ${fmt(sm.last_downloaded)}${sm.errors ? ` · ${sm.errors} Fehler` : ""}`
          : "Sitemap noch nicht eingereicht – in der Search Console unter „Sitemaps“ eintragen"} · Stand {fmt(r.at)} · {r.property}</p>
      <div className="mt-3 divide-y divide-white/5" data-testid={`gsc-${slug}-urls`}>
        {r.urls.map((u) => (
          <div key={u.url} className="py-2 grid sm:grid-cols-[150px_120px_1fr] gap-2 items-center text-xs" data-testid={`gsc-${slug}-url-${u.slug}`}>
            <a href={u.url} target="_blank" rel="noreferrer" className="font-semibold truncate hover:underline">{u.title}</a>
            <Badge u={u} />
            <span className="text-eb-muted truncate">{u.error || u.coverage || "–"}{u.last_crawl ? ` · zuletzt besucht ${fmt(u.last_crawl)}` : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Account({ s, host, slug }) {
  if (s.mode === "oauth") return (
    <div className="flex items-center gap-2 mt-2 text-xs flex-wrap" data-testid={`gsc-${slug}-account`}>
      <span className="text-eb-muted">Verbunden mit:</span><b data-testid={`gsc-${slug}-account-email`}>{s.account_email || "Google-Konto"}</b>
      <span className="text-eb-muted">seit {fmt(s.connected_at)}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-2 mt-2 text-xs flex-wrap" data-testid={`gsc-${slug}-account`}>
      <span className="text-eb-muted">Dienstkonto:</span><CopyLine value={s.client_email} testid={`gsc-${slug}-client-email`} />
      <a className={btn} href={`https://search.google.com/search-console/users?resource_id=sc-domain:${host}`} target="_blank" rel="noreferrer" data-testid={`gsc-${slug}-open-users`}><ExternalLink size={12} /> Search Console: Nutzer</a>
    </div>
  );
}

export function SiteGoogle({ slug, host, standalone }) {
  const api = `${process.env.REACT_APP_BACKEND_URL}/api/sites/${slug}/google`;
  const isAdmin = useIsAdmin();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const back = useRef(false);
  const load = useCallback(() => axios.get(api).then((r) => setS(r.data)).catch((e) => setMsg(err(e, "Google-Status nicht ladbar"))), [api]);
  const act = async (k, fn) => {
    setBusy(k); setMsg("");
    try { setS((await fn()).data); } catch (e) { setMsg(err(e, "Aktion fehlgeschlagen")); }
    setBusy("");
  };
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const pending = sessionStorage.getItem(PENDING_KEY);
    if (window.location.pathname !== "/auth/google" || pending !== slug || back.current) { load(); return; }
    back.current = true;
    sessionStorage.removeItem(PENDING_KEY);
    window.history.replaceState(null, "", `/?view=${slug}`);
    if (q.get("error")) { setMsg(`Google-Verbindung abgebrochen (${q.get("error")})`); load(); return; }
    act("finish", () => axios.post(`${api}/oauth/finish`, { code: q.get("code"), state: q.get("state"), redirect_uri: redirectUri() })).then(load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);
  const connect = async () => {
    setBusy("connect"); setMsg("");
    try { sessionStorage.setItem(PENDING_KEY, slug); window.location.href = (await axios.post(`${api}/oauth/start`, { redirect_uri: redirectUri() })).data.url; }
    catch (e) { setMsg(err(e, "Verbinden nicht möglich")); setBusy(""); }
  };
  if (!s) return msg ? <p className="text-sm text-rose-400 mt-4" data-testid={`gsc-${slug}-msg`}>{msg}</p> : <Loader2 className="animate-spin mt-4" size={16} />;
  return (
    <div className={standalone ? "" : "mt-5 pt-4 border-t border-white/5"} data-testid={`gsc-${slug}-panel`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm"><b>Bei Google aufgenommen</b> <span className="text-eb-muted text-xs">· aus der Search Console dieser Website</span></div>
        {s.configured && <div className="flex gap-2 flex-wrap">
          {s.connected && <button className={btn} disabled={!!busy} onClick={() => act("refresh", () => axios.post(`${api}/refresh`))} data-testid={`gsc-${slug}-refresh`}>
            {busy === "refresh" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Jetzt bei Google abfragen</button>}
          {isAdmin && <button className={`${btn} text-rose-300 border-rose-400/30`} disabled={!!busy} data-testid={`gsc-${slug}-key-delete`}
            onClick={() => window.confirm("Google-Zugang aus Sunworker entfernen?") && act("del", () => axios.delete(`${api}/credentials`))}><Trash2 size={13} /> Zugang entfernen</button>}
        </div>}
      </div>
      {!s.configured && (isAdmin
        ? <><p className="text-xs text-eb-muted mt-2">Einmalig einrichten, danach siehst du hier, welche Seiten Google schon aufgenommen hat (Seiten werden automatisch aus {s.sitemap} gelesen).</p><KeyForm api={api} slug={slug} onSaved={setS} /></>
        : <p className="text-xs text-eb-muted mt-2" data-testid={`gsc-${slug}-admin-only`}>Noch nicht eingerichtet – nur ein Admin kann den Google-Zugang einrichten.</p>)}
      {s.configured && s.mode === "oauth" && !s.connected && isAdmin && (
        <div data-testid={`gsc-${slug}-oauth-setup`}>
          <p className="text-xs text-eb-muted mt-2">OAuth-Client gespeichert (Projekt {s.project_id || "–"}). Noch 4 Schritte:</p>
          <OAuthSteps project={s.project_id} slug={slug} />
          <button className={`${btn} mt-3 bg-white text-black hover:bg-white/90 border-white`} disabled={!!busy} onClick={connect} data-testid={`gsc-${slug}-connect`}>
            {busy === "connect" || busy === "finish" ? <Loader2 size={13} className="animate-spin" /> : <LogIn size={13} />} Mit Google verbinden</button>
        </div>
      )}
      {s.connected && <Account s={s} host={host} slug={slug} />}
      {s.configured && s.error && <p className="text-xs text-amber-300 mt-3" data-testid={`gsc-${slug}-error`}>{s.error.msg} <span className="text-eb-muted">· {fmt(s.error.at)}</span></p>}
      {s.connected && (s.report ? <Report r={s.report} slug={slug} /> : <p className="text-xs text-eb-muted mt-3" data-testid={`gsc-${slug}-no-report`}>Noch kein Bericht – „Jetzt bei Google abfragen“ tippen.</p>)}
      {s.connected && s.mode === "oauth" && s.error && isAdmin && (
        <button className={`${btn} mt-2`} disabled={!!busy} onClick={connect} data-testid={`gsc-${slug}-reconnect`}><LogIn size={13} /> Neu verbinden</button>
      )}
      {msg && <p className="text-xs text-rose-400 mt-2" data-testid={`gsc-${slug}-msg`}>{msg}</p>}
    </div>
  );
}
